// ApprovalChainEditor — named-signatory chain form (plan §3).
//
// Displays every role required by the project's regulatory flags as
// an editable row. The architect fills names / marks N-A / adds notes.
// On save the full list is PATCHed to the backend and the refreshed
// ProjectContext is returned so the UI picks up derived defaults.
//
// Validation mirrors NamedSignatory Pydantic rules:
//   * NAMED  → name non-empty
//   * PLACEHOLDER → name must be null
//   * NOT_APPLICABLE → not_applicable_reason non-empty

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Save,
  Users,
  X,
} from 'lucide-react'

import { api } from '@/lib/api'
import { toast } from '@/hooks/use-toast'
import type {
  NamedSignatory,
  RequiredSignatoryRole,
  SignatoryStatus,
} from '@/types'

interface ApprovalChainEditorProps {
  projectId: string
  onSaved?: () => void
}

type RowState = NamedSignatory & { _expanded: boolean }

function makeRowFromRequired(
  role: RequiredSignatoryRole,
  existing?: NamedSignatory,
): RowState {
  if (existing) {
    return { ...existing, _expanded: false }
  }
  return {
    role_key: role.role_key,
    role_display: role.role_display,
    required_by: role.required_by,
    status: 'placeholder',
    name: null,
    title: null,
    organization: null,
    signature_date: null,
    not_applicable_reason: null,
    notes: null,
    _expanded: true,
  }
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

function isFutureDate(d: string): boolean {
  return d > todayStr()
}

export function ApprovalChainEditor({ projectId, onSaved }: ApprovalChainEditorProps) {
  const queryClient = useQueryClient()

  const intakeQuery = useQuery({
    queryKey: ['intake', projectId],
    queryFn: () => api.getIntake(projectId),
    enabled: Boolean(projectId),
  })

  const requiredQuery = useQuery({
    queryKey: ['required-signatories', projectId],
    queryFn: () => api.getRequiredSignatories(projectId),
    enabled: Boolean(projectId),
  })

  // Stabilise the array references so the row-init `useEffect`
  // re-runs only when the underlying query data actually changes,
  // not on every render. (Reading `.data?.x ?? []` directly returns
  // a fresh empty array on each render, which would loop the effect.)
  const existingSignatories = useMemo<NamedSignatory[]>(
    () => intakeQuery.data?.intake?.signatories ?? [],
    [intakeQuery.data],
  )
  const requiredRoles = useMemo<RequiredSignatoryRole[]>(
    () => requiredQuery.data?.required_roles ?? [],
    [requiredQuery.data],
  )

  const [rows, setRows] = useState<RowState[]>([])
  const [touched, setTouched] = useState<Set<number>>(new Set())

  // Initialise rows whenever the required-roles set or the persisted
  // signatories change. Resets `touched` so post-save validation
  // doesn't flag fields the architect hasn't re-edited.
  useEffect(() => {
    if (requiredRoles.length === 0) return
    const next = requiredRoles.map((role) => {
      const existing = existingSignatories.find(
        (s) => s.role_key === role.role_key,
      )
      return makeRowFromRequired(role, existing)
    })
    setRows(next)
    setTouched(new Set())
  }, [requiredRoles, existingSignatories])

  const mutate = useMutation({
    mutationFn: () => {
      const payload = rows.map((r) => {
        const { _expanded, ...clean } = r
        return clean
      })
      return api.patchSignatories(projectId, { signatories: payload })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake', projectId] })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      toast({ variant: 'success', title: 'Approval chain saved' })
      onSaved?.()
    },
  })

  const setRow = (idx: number, updater: (prev: RowState) => RowState) => {
    setRows((prev) => {
      const next = [...prev]
      next[idx] = updater(next[idx])
      return next
    })
    setTouched((prev) => new Set(prev).add(idx))
  }

  const errors = useMemo(() => {
    const out: Record<number, string[]> = {}
    rows.forEach((row, idx) => {
      const e: string[] = []
      if (row.status === 'named' && !row.name?.trim()) {
        e.push('Name is required for named signatories')
      }
      if (row.status === 'placeholder' && row.name) {
        e.push('Placeholder signatories must not have a name')
      }
      if (row.status === 'not_applicable' && !row.not_applicable_reason?.trim()) {
        e.push('A reason is required when marking not applicable')
      }
      if (row.signature_date && isFutureDate(row.signature_date)) {
        e.push('Signature date cannot be in the future')
      }
      if (e.length) out[idx] = e
    })
    return out
  }, [rows])

  const hasErrors = Object.keys(errors).length > 0
  const allResolved = rows.every(
    (r) => r.status === 'named' || r.status === 'not_applicable',
  )

  if (requiredQuery.isLoading || intakeQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading approval chain…
      </div>
    )
  }

  if (requiredRoles.length === 0) {
    return (
      <div className="alm-card text-sm text-slate-400">
        No regulatory flags selected that require named signatories.
      </div>
    )
  }

  return (
    <div className="alm-fadein space-y-4">
      <PageHeader
        icon={<Users className="h-5 w-5" />}
        title="Approval Chain"
        subtitle={
          allResolved
            ? 'All required signatory slots are resolved.'
            : 'Fill every required signatory slot or mark not applicable with a reason.'
        }
      />

      <div className="space-y-2">
        {rows.map((row, idx) => (
          <SignatoryRow
            key={row.role_key}
            row={row}
            errors={errors[idx] ?? []}
            touched={touched.has(idx)}
            onChange={(updater) => setRow(idx, updater)}
          />
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="text-xs text-slate-500">
          {allResolved ? (
            <span className="text-emerald-400 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> All slots resolved
            </span>
          ) : (
            <span className="flex items-center gap-1 text-amber-400">
              <AlertTriangle className="h-3 w-3" />
              {rows.filter((r) => r.status === 'placeholder').length} unfilled
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() => mutate.mutate()}
          disabled={mutate.isPending || hasErrors}
          className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500"
        >
          {mutate.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save approval chain
        </button>
      </div>

      {mutate.error ? (
        <p className="text-xs text-red-300" role="alert">
          {(mutate.error as Error).message}
        </p>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------- //
// SignatoryRow — per-role editable card
// ---------------------------------------------------------------------- //
function SignatoryRow({
  row,
  errors,
  touched,
  onChange,
}: {
  row: RowState
  errors: string[]
  touched: boolean
  onChange: (updater: (prev: RowState) => RowState) => void
}) {
  const toggleExpand = () => onChange((r) => ({ ...r, _expanded: !r._expanded }))
  const setStatus = (status: SignatoryStatus) => {
    onChange((r) => {
      const next: RowState = { ...r, status, _expanded: true }
      if (status === 'placeholder') {
        next.name = null
        next.title = null
        next.organization = null
        next.signature_date = null
        next.not_applicable_reason = null
      } else if (status === 'named') {
        next.not_applicable_reason = null
      } else if (status === 'not_applicable') {
        next.name = null
        next.title = null
        next.organization = null
        next.signature_date = null
      }
      return next
    })
  }

  const statusPill = (() => {
    switch (row.status) {
      case 'named':
        return (
          <span className="alm-chip alm-chip-ok">
            <CheckCircle2 className="h-3 w-3" /> Named
          </span>
        )
      case 'not_applicable':
        return (
          <span className="alm-chip text-slate-400">
            <X className="h-3 w-3" /> N/A
          </span>
        )
      default:
        return (
          <span className="alm-chip alm-chip-warn">
            <AlertTriangle className="h-3 w-3" /> Unfilled
          </span>
        )
    }
  })()

  return (
    <div
      className={`rounded-lg border border-slate-700/60 bg-slate-900/60 p-4 ${errors.length > 0 && touched ? 'border-red-700/50' : ''}`}
      data-testid={`signatory-row-${row.role_key}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-semibold text-slate-100">{row.role_display}</span>
            {statusPill}
          </div>
          <div className="text-[11px] text-slate-500">
            Required by: {row.required_by.join(' · ')}
          </div>
        </div>
        <button
          type="button"
          onClick={toggleExpand}
          className="text-slate-400 hover:text-white"
          aria-label={row._expanded ? 'Collapse' : 'Expand'}
        >
          {row._expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Expanded body */}
      {row._expanded && (
        <div className="mt-3 space-y-3">
          {/* Status selector */}
          <div className="flex flex-wrap gap-2">
            <StatusButton
              active={row.status === 'placeholder'}
              onClick={() => setStatus('placeholder')}
              label="Unfilled"
            />
            <StatusButton
              active={row.status === 'named'}
              onClick={() => setStatus('named')}
              label="Named"
            />
            <StatusButton
              active={row.status === 'not_applicable'}
              onClick={() => setStatus('not_applicable')}
              label="Not applicable"
            />
          </div>

          {/* Named fields */}
          {row.status === 'named' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field
                label="Name *"
                value={row.name ?? ''}
                onChange={(v) => onChange((r) => ({ ...r, name: v || null }))}
                placeholder="e.g. Jane Doe"
              />
              <Field
                label="Title"
                value={row.title ?? ''}
                onChange={(v) => onChange((r) => ({ ...r, title: v || null }))}
                placeholder="e.g. Chief Information Security Officer"
              />
              <Field
                label="Organization"
                value={row.organization ?? ''}
                onChange={(v) => onChange((r) => ({ ...r, organization: v || null }))}
                placeholder="e.g. Acme Corp"
              />
              <Field
                label="Signature date"
                type="date"
                value={row.signature_date ?? ''}
                onChange={(v) => onChange((r) => ({ ...r, signature_date: v || null }))}
              />
            </div>
          )}

          {/* Not applicable reason */}
          {row.status === 'not_applicable' && (
            <div>
              <label className="text-[10.5px] uppercase tracking-wider text-slate-500">
                Reason *
              </label>
              <textarea
                rows={2}
                value={row.not_applicable_reason ?? ''}
                onChange={(e) =>
                  onChange((r) => ({ ...r, not_applicable_reason: e.target.value || null }))
                }
                placeholder="Why is this signatory not applicable to the project?"
                className="mt-1 w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
              />
            </div>
          )}

          {/* Notes (all statuses) */}
          <div>
            <label className="text-[10.5px] uppercase tracking-wider text-slate-500">
              Notes
            </label>
            <textarea
              rows={1}
              value={row.notes ?? ''}
              onChange={(e) => onChange((r) => ({ ...r, notes: e.target.value || null }))}
              placeholder="Optional notes"
              className="mt-1 w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
            />
          </div>

          {/* Validation errors */}
          {errors.length > 0 && touched && (
            <div className="space-y-1">
              {errors.map((err) => (
                <p key={err} className="text-xs text-red-300 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {err}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Collapsed summary */}
      {!row._expanded && row.status === 'named' && (
        <div className="mt-2 text-sm text-slate-300">
          {row.name}
          {row.title ? ` — ${row.title}` : ''}
          {row.organization ? `, ${row.organization}` : ''}
          {row.signature_date ? ` · signed ${row.signature_date}` : ''}
        </div>
      )}
      {!row._expanded && row.status === 'not_applicable' && (
        <div className="mt-2 text-sm text-slate-400">
          Reason: {row.not_applicable_reason}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------- //
// Small helpers
// ---------------------------------------------------------------------- //
function StatusButton({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${active ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300' : 'border-slate-700 text-slate-400 hover:bg-white/5 hover:text-slate-200'}`}
    >
      {label}
    </button>
  )
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
}) {
  return (
    <div>
      <label className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
      />
    </div>
  )
}

function PageHeader({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-indigo-400">{icon}</div>
      <div>
        <h2 className="text-xl font-bold tracking-tight text-slate-100">{title}</h2>
        <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>
      </div>
    </div>
  )
}